import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign, UserMinus, Trophy, MessageSquare, Loader2,
  Calendar, Download, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * Admin → Reports tab (iter126).
 *
 * Retention Digest with date-range filtering across 4 reports:
 *   • Revenue & Signups   • Churn Risk
 *   • Top Referring Plans • Messages per Agent
 *
 * Each report auto-refreshes when the date range changes. CSV export is
 * available per report so admins can drop numbers into their BI tools.
 */

const PRESETS = [
  { id: 'today',  label: 'Today',    days: 0 },
  { id: '7d',     label: 'Last 7d',  days: 7 },
  { id: '30d',    label: 'Last 30d', days: 30 },
  { id: '90d',    label: 'Last 90d', days: 90 },
];

const toISO = (d) => d.toISOString().slice(0, 10);

const downloadCSV = (rows, columns, filename) => {
  if (!rows?.length) { toast.info('Nothing to export'); return; }
  const headers = columns.join(',');
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = r[c];
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(',')
  ).join('\n');
  const blob = new Blob([headers + '\n' + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ReportCard = ({ title, description, icon: Icon, iconColor, columns, rows, totals, loading, onExport, children }) => (
  <Card className="bg-white border-slate-200 shadow-sm">
    <CardHeader className="flex flex-row items-start justify-between gap-4">
      <div className="min-w-0">
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          {title}
        </CardTitle>
        <CardDescription className="text-slate-500 mt-1">{description}</CardDescription>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {children}
        <Button
          variant="outline" size="sm" onClick={onExport}
          disabled={loading || !rows?.length}
          data-testid={`export-${title.toLowerCase().replace(/\s+/g, '-')}`}
          className="border-slate-300"
        >
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      {totals && (
        <div className="flex gap-4 mb-3 flex-wrap">
          {Object.entries(totals).map(([k, v]) => (
            <div key={k} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm">
              <div className="text-slate-500 text-[11px] uppercase tracking-wide">{k.replace(/_/g, ' ')}</div>
              <div className="font-mono text-slate-900 font-semibold">
                {typeof v === 'number' && (k.includes('revenue') || k.includes('$')) ? `$${v.toFixed(2)}` : v}
              </div>
            </div>
          ))}
        </div>
      )}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : rows?.length ? (
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b sticky top-0">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="text-left px-3 py-2 font-medium capitalize">{c.replace(/_/g, ' ')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${title}-${i}`} className="border-b last:border-b-0 hover:bg-slate-50">
                  {columns.map((c) => (
                    <td key={c} className="px-3 py-2 text-slate-700">
                      {typeof r[c] === 'number' && (c.includes('revenue') || c.includes('price'))
                        ? `$${r[c].toFixed(2)}`
                        : (r[c] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-500 text-center py-6">No data for this range</p>
      )}
    </CardContent>
  </Card>
);

export const AdminReportsTab = () => {
  // Date range
  const [preset, setPreset] = useState('30d');
  const today = toISO(new Date());
  const [start, setStart] = useState(toISO(new Date(Date.now() - 30 * 86400000)));
  const [end, setEnd] = useState(today);

  // Report state
  const [revenue, setRevenue] = useState({ rows: [], columns: [], totals: {} });
  const [churn, setChurn] = useState({ rows: [], columns: [], totals: {} });
  const [topPlans, setTopPlans] = useState({ rows: [], columns: [], totals: {} });
  const [agentUse, setAgentUse] = useState({ rows: [], columns: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [minSilent, setMinSilent] = useState(14);

  const applyPreset = (id) => {
    setPreset(id);
    if (id === 'today') {
      setStart(today);
      setEnd(today);
      return;
    }
    const days = PRESETS.find((p) => p.id === id)?.days ?? 30;
    setStart(toISO(new Date(Date.now() - days * 86400000)));
    setEnd(today);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    const qs = `?start=${start}&end=${end}`;
    try {
      const [rev, churnR, plans, agents] = await Promise.all([
        api.get(`/admin/reports/revenue${qs}`),
        api.get(`/admin/reports/churn-risk${qs}&min_days_silent=${minSilent}`),
        api.get(`/admin/reports/top-referring-plans${qs}`),
        api.get(`/admin/reports/agent-usage${qs}`),
      ]);
      setRevenue(rev.data);
      setChurn(churnR.data);
      setTopPlans(plans.data);
      setAgentUse(agents.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [start, end, minSilent]);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-6" data-testid="admin-reports-tab">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          Retention & Growth Reports
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Pick a date range and get four snapshots of the health of your platform. All reports are exportable as CSV.
        </p>
      </div>

      {/* Date range picker */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex gap-1">
              {PRESETS.map((p) => (
                <Button
                  key={p.id}
                  variant={preset === p.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyPreset(p.id)}
                  className={preset === p.id ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-slate-300'}
                  data-testid={`reports-preset-${p.id}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs text-slate-500">Start</Label>
                <Input
                  type="date" value={start}
                  onChange={(e) => { setPreset('custom'); setStart(e.target.value); }}
                  data-testid="reports-start-input"
                  className="w-40"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">End</Label>
                <Input
                  type="date" value={end}
                  onChange={(e) => { setPreset('custom'); setEnd(e.target.value); }}
                  data-testid="reports-end-input"
                  className="w-40"
                />
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
              <Calendar className="w-3 h-3" />
              {start} → {end}
            </div>
          </div>
        </CardContent>
      </Card>

      <ReportCard
        title="Revenue & Signups"
        description="Daily paid revenue, order count, and new user signups."
        icon={DollarSign} iconColor="text-emerald-600"
        columns={revenue.columns} rows={revenue.rows} totals={revenue.totals}
        loading={loading}
        onExport={() => downloadCSV(revenue.rows, revenue.columns, `revenue_${start}_${end}.csv`)}
      />

      <ReportCard
        title="Churn Risk"
        description={`Paid accounts with zero chatbot activity in the last ${minSilent} days.`}
        icon={UserMinus} iconColor="text-red-600"
        columns={churn.columns} rows={churn.rows} totals={churn.totals}
        loading={loading}
        onExport={() => downloadCSV(churn.rows, churn.columns, `churn_risk_${start}_${end}.csv`)}
      >
        <div className="flex items-center gap-1">
          <Label className="text-xs text-slate-500 whitespace-nowrap">Silent days</Label>
          <Input
            type="number" min={1} max={365} value={minSilent}
            onChange={(e) => setMinSilent(parseInt(e.target.value, 10) || 14)}
            className="w-16 h-8 text-sm"
            data-testid="churn-silent-days"
          />
        </div>
      </ReportCard>

      <ReportCard
        title="Top Referring Plans"
        description="Plans by paid conversions in the selected window."
        icon={Trophy} iconColor="text-amber-600"
        columns={topPlans.columns} rows={topPlans.rows} totals={topPlans.totals}
        loading={loading}
        onExport={() => downloadCSV(topPlans.rows, topPlans.columns, `top_plans_${start}_${end}.csv`)}
      />

      <ReportCard
        title="Messages per Agent"
        description="Which AI Employees are actually being used vs sitting idle."
        icon={MessageSquare} iconColor="text-blue-600"
        columns={agentUse.columns} rows={agentUse.rows} totals={agentUse.totals}
        loading={loading}
        onExport={() => downloadCSV(agentUse.rows, agentUse.columns, `agent_usage_${start}_${end}.csv`)}
      />
    </div>
  );
};

export default AdminReportsTab;
